import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconDefinition,
  faPen,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { mergeClassNames } from 'shared/utils/mergeClassNames';
import { WIZARD_THEMES, WizardThemeKey } from './WizardHeader';

export type IdentityBadgeTone = 'success' | 'info' | 'neutral';

export interface IdentityBadge {
  tone: IdentityBadgeTone;
  label: string;
}

interface SidebarIdentityProps {
  theme: WizardThemeKey;
  kickerIcon?: IconDefinition;
  kickerLabel?: string;
  breadcrumb?: string;
  title: string;
  subtitle?: string | React.ReactNode;
  progress?: { answered: number; total: number };
  badge?: IdentityBadge;
  /**
   * Optional inline editor. Rendered as a function so the host can wire
   * the editor's Cancel / Save callbacks to `close`, collapsing the
   * card back to the identity view.
   */
  editorSlot?: (close: () => void) => React.ReactNode;
  editable?: boolean;
}

const badgeToneStyles = (tone: IdentityBadgeTone) => {
  switch (tone) {
    case 'success':
      return css`
        background: ${COLORS.successBackground};
        color: ${COLORS.success};
      `;
    case 'info':
      return css`
        background: ${COLORS.ctaPrimaryBackground};
        color: ${COLORS.ctaPrimary};
      `;
    default:
      return css`
        background: ${COLORS.greyLight};
        color: ${COLORS.greyDark};
      `;
  }
};

interface CardProps {
  accent: string;
  accentBackground: string;
}

const Card = styled.div<CardProps>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  /* Top block: name + edit affordance + status meta. */
  .head {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: ${SPACERS.small} ${SPACERS.medium};
    background: ${COLORS.whiteBase};
    border-bottom: 1px solid ${COLORS.grayBase};
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
  }
  .title-row .title {
    flex: 1 1 auto;
    min-width: 0;
  }

  /* Divider band below the head — 'Program Profile' on the program
     modal, the parent program name on snapshots. */
  .kicker-row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: ${SPACERS.tiny} ${SPACERS.medium};
    background: ${p => p.accent}14;
    border-bottom: 1px solid ${COLORS.grayBase};
  }

  .kicker {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${COLORS.greyDarkest};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    min-width: 0;
    svg {
      color: ${p => p.accent};
    }
  }

  .badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .badge.tone-success {
    ${badgeToneStyles('success')}
  }
  .badge.tone-info {
    ${badgeToneStyles('info')}
  }
  .badge.tone-neutral {
    ${badgeToneStyles('neutral')}
  }

  /* Icon-only edit affordance living inline in the kicker row. Sized
     tight so it aligns with the badge baseline. Tinted from the
     accent styled-component prop so it stays in sync with the theme. */
  .edit-button.MuiIconButton-root {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0 !important;
    border-radius: 999px !important;
    border: 1px solid ${p => p.accent}59;
    background: ${COLORS.whiteBase};
    transition: background 0.15s ease, border-color 0.15s ease;

    svg {
      color: ${p => p.accent} !important;
      padding: 0 !important;
      width: 11px !important;
      height: 11px !important;
    }
    svg path {
      fill: ${p => p.accent} !important;
      stroke: ${p => p.accent} !important;
    }

    &:hover {
      background: ${p => p.accent}1F;
      border-color: ${p => p.accent};
    }
    &.is-active {
      background: ${p => p.accent};
      border-color: ${p => p.accent};
    }
    &.is-active svg {
      color: ${COLORS.whiteBase} !important;
    }
    &.is-active svg path {
      fill: ${COLORS.whiteBase} !important;
      stroke: ${COLORS.whiteBase} !important;
    }
  }

  .title {
    color: ${COLORS.greyDarkest};
    line-height: 1.25;
    font-size: 15px;
    word-break: break-word;
  }

  .subtitle {
    color: ${COLORS.greyDark};
    line-height: 1.4;
  }

  .progress {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }
  .progress-bar {
    flex: 1 1 auto;
    height: 4px;
    border-radius: 999px;
    background: ${p => p.accent}1F;
    overflow: hidden;
  }
  .progress-bar-inner {
    height: 100%;
    background: ${p => p.accent};
    transition: width 240ms ease;
  }
  .progress-text {
    color: ${COLORS.greyDark};
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .editor-panel {
    margin-top: ${SPACERS.tiny};
    padding-top: ${SPACERS.small};
    border-top: 1px dashed ${p => p.accent}66;
  }
`;

const SidebarIdentity: React.FC<SidebarIdentityProps> = ({
  theme,
  kickerIcon,
  kickerLabel,
  breadcrumb,
  title,
  subtitle,
  progress,
  badge,
  editorSlot,
  editable,
}) => {
  const [editing, setEditing] = useState(false);
  const showEditToggle =
    (editable ?? Boolean(editorSlot)) && Boolean(editorSlot);
  const tokens = WIZARD_THEMES[theme];
  const pct =
    progress && progress.total > 0
      ? Math.round((progress.answered / progress.total) * 100)
      : 0;

  return (
    <Card accent={tokens.accent} accentBackground={tokens.accentBackground}>
      <div className="head">
        <div className="title-row">
          <TrueImpactText variant="body-bold" className="title">
            {title}
          </TrueImpactText>
          {badge && (
            <span className={mergeClassNames('badge', `tone-${badge.tone}`)}>
              {badge.label}
            </span>
          )}
          {showEditToggle && (
            <TrueImpactIconButton
              className={mergeClassNames('edit-button', editing && 'is-active')}
              icon={editing ? faXmark : faPen}
              label={editing ? 'Close editor' : 'Edit program details'}
              onClick={() => setEditing(v => !v)}
            />
          )}
        </div>

        {subtitle && (
          <TrueImpactText variant="body-small" className="subtitle">
            {subtitle}
          </TrueImpactText>
        )}

        {progress && progress.total > 0 && !editing && (
          <div className="progress">
            <div className="progress-bar">
              <div
                className="progress-bar-inner"
                style={{ width: `${pct}%` }}
              />
            </div>
            <TrueImpactText variant="body-small" className="progress-text">
              {progress.answered}/{progress.total} ({pct}%)
            </TrueImpactText>
          </div>
        )}

        {editing && showEditToggle && editorSlot && (
          <div className="editor-panel">
            {editorSlot(() => setEditing(false))}
          </div>
        )}
      </div>

      <div className="kicker-row">
        <span className="kicker">
          {kickerIcon && <FontAwesomeIcon icon={kickerIcon} />}
          {breadcrumb ?? kickerLabel ?? tokens.kicker}
        </span>
      </div>
    </Card>
  );
};

export default SidebarIdentity;
